import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useScreenInit } from './useScreenInit';
import { ScrollToTop } from './components/ScrollToTop';
import { LanguageProvider } from './lib/LanguageContext';
import { RouteFallback } from './components/RouteFallback';

/**
 * Route-level code splitting. Each page ships as its own chunk so the first
 * paint only downloads the route the visitor actually landed on.
 * Pages use named exports, hence the `.then` remap.
 */
const Home = lazy(() => import('./pages/Home').then(m => ({
  default: m.Home
})));
const Story = lazy(() => import('./pages/Story').then(m => ({
  default: m.Story
})));
const Services = lazy(() => import('./pages/Services').then(m => ({
  default: m.Services
})));
const MeditationBetweenWorlds = lazy(() => import('./pages/MeditationBetweenWorlds').then(m => ({
  default: m.MeditationBetweenWorlds
})));
const SoundInfusedRenewal = lazy(() => import('./pages/SoundInfusedRenewal').then(m => ({
  default: m.SoundInfusedRenewal
})));
const Sopotje = lazy(() => import('./pages/Sopotje').then(m => ({
  default: m.Sopotje
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const Testimonials = lazy(() => import('./pages/Testimonials').then(m => ({
  default: m.Testimonials
})));
const Podcasts = lazy(() => import('./pages/Podcasts').then(m => ({
  default: m.Podcasts
})));
const Meditation = lazy(() => import('./pages/Meditation').then(m => ({
  default: m.Meditation
})));
const CreativeCorner = lazy(() => import('./pages/CreativeCorner').then(m => ({
  default: m.CreativeCorner
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
function LegacyJournalPostRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={slug ? `/journal/${slug}` : '/journal'} replace data-id="element-0" data-mp="35gkz19r83iz" />;
}
function AppContent() {
  useScreenInit();
  return <>
      <ScrollToTop data-id="element-1" data-mp="3ucjdpx58qhvb" />
      <Suspense fallback={<RouteFallback data-id="element-3" data-mp="d4e57s05daw6" />} data-id="element-2" data-mp="3uf5gljh6s977">
        <Routes data-id="element-4" data-mp="d3mn9jzh3bod">
          <Route path="/" element={<Home data-id="element-6" data-mp="d2l6qzzotwz9" />} data-id="element-5" data-mp="d45sq17xs7li" />
          <Route path="/meet-milangela" element={<Story data-id="element-8" data-mp="86w6svjrae2f" />} data-id="element-7" data-mp="85ui3o9tht28" />
          <Route path="/start-your-journey" element={<Services data-id="element-10" data-mp="3gl4qtf87pwiz" />} data-id="element-9" data-mp="86cm572q1bkp" />
          <Route path="/start-your-journey/meditation-between-worlds" element={<MeditationBetweenWorlds data-id="element-12" data-mp="3bn2zy84joswy" />} data-id="element-11" data-mp="3gmfulf1nr4a3" />
          <Route path="/start-your-journey/sound-infused-renewal" element={<SoundInfusedRenewal data-id="element-14" data-mp="3qcipjkscasb2" />} data-id="element-13" data-mp="3bk736n4foaa3" />
          <Route path="/start-your-journey/sopotje" element={<Sopotje data-id="element-16" data-mp="3lj6bg4lae3wk" />} data-id="element-15" data-mp="3ljxukfxp695y" />
          <Route path="/journal" element={<Blog data-id="element-18" data-mp="2xu3ii0l7comr" />} data-id="element-17" data-mp="2xuwuph2xk6ca" />
          <Route path="/journal/:slug" element={<BlogPost data-id="element-20" data-mp="2xsk3edba8jmk" />} data-id="element-19" data-mp="2xtlnt8e6uigy" />
          <Route path="/testimonials" element={<Testimonials data-id="element-22" data-mp="2svah36j6wo2y" />} data-id="element-21" data-mp="2swbsjc44bcl7" />
          <Route path="/podcasts" element={<Podcasts data-id="element-24" data-mp="18d9c849dngr4" />} data-id="element-23" data-mp="2stzdb6pqvgbu" />
          <Route path="/guided-meditations" element={<Meditation data-id="element-26" data-mp="18by8u5jugb0j" />} data-id="element-25" data-mp="18czlufkf77kw" />
          <Route path="/creative-corner" element={<CreativeCorner data-id="element-28" data-mp="1d62iwxoeoz5u" />} data-id="element-27" data-mp="1d4zt6t2tl2ri" />
          <Route path="/lets-connect" element={<Contact data-id="element-30" data-mp="ygd2xwbnmqli" />} data-id="element-29" data-mp="1d6ayixbwyikq" />

          {/* Legacy URL redirects — mirror these as 301s in WordPress */}
          <Route path="/milangela" element={<Navigate to="/meet-milangela" replace data-id="element-32" data-mp="13cbwrzfr50tb" />} data-id="element-31" data-mp="13dwu1wtgaxuv" />
          <Route path="/services" element={<Navigate to="/start-your-journey" replace data-id="element-34" data-mp="1w33ookpsb3au" />} data-id="element-33" data-mp="1r5k8vgdfuigs" />
          <Route path="/services/meditation-between-worlds" element={<Navigate to="/start-your-journey/meditation-between-worlds" replace data-id="element-36" data-mp="1i5ngb3nxgzgg" />} data-id="element-35" data-mp="1w19d3x0eknjf" />
          <Route path="/services/sound-infused-renewal" element={<Navigate to="/start-your-journey/sound-infused-renewal" replace data-id="element-38" data-mp="aq9benbqqhav" />} data-id="element-37" data-mp="6o5uvka7smiq" />
          <Route path="/services/sopotje" element={<Navigate to="/start-your-journey/sopotje" replace data-id="element-40" data-mp="m5dx68xrfkud" />} data-id="element-39" data-mp="qz8i1fp3q5v1" />
          <Route path="/blog" element={<Navigate to="/journal" replace data-id="element-42" data-mp="h842oevikizo" />} data-id="element-41" data-mp="h6sywf22jb8k" />
          <Route path="/blog/:slug" element={<LegacyJournalPostRedirect data-id="element-44" data-mp="c9amufsr8fwc" />} data-id="element-43" data-mp="c89cyenh5zg7" />
          <Route path="/meditation" element={<Navigate to="/guided-meditations" replace data-id="element-46" data-mp="19uyiuzlasw7c" />} data-id="element-45" data-mp="c66cf0op4nx8" />
          <Route path="/contact" element={<Navigate to="/lets-connect" replace data-id="element-48" data-mp="14xgbvequcr1z" />} data-id="element-47" data-mp="14x6h7duupvpy" />
          <Route path="*" element={<Navigate to="/" replace data-id="element-50" data-mp="zwiuv5hki322" />} data-id="element-49" data-mp="zx2c0jze8nf1" />
        </Routes>
      </Suspense>
    </>;
}
export function App() {
  return <LanguageProvider data-id="element-51" data-mp="vybam0nwajm4">
      <BrowserRouter data-id="element-52" data-mp="3ko2t2sdw98wq">
        <AppContent data-id="element-53" data-mp="3kokndjgzxd20" />
      </BrowserRouter>
    </LanguageProvider>;
}